import React, { useState, useEffect } from "react";
import styles from "./css/UsersPage.module.css";
import CityManagementTable from "@/components/tables/CityManagementTable";
import { SearchBar } from "@/components/bars/SearchBar";
import { Button } from "@/components/Buttons";
import { CityData } from "@/types";
import { useSelector, useDispatch } from "react-redux";
import { useGetCityStats } from "@/hooks/city/useGetCityStats";
import { RootState } from "@/store/store";
import { setToast } from "@/store/systemSlice";
import { useTranslation } from 'react-i18next';
import AddCityModal from "@/components/modals/AddCityModal";
import { useNavigate } from "react-router-dom";
import { getEntityName } from "@/utils/getEntityName";

interface SuperAdminCitiesPageProps {
  cityParametersMode?: boolean;
}

export const SuperAdminCitiesPage: React.FC<SuperAdminCitiesPageProps> = ({ cityParametersMode = false }) => {
  const { t } = useTranslation('SuperAdminCities');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const city = useSelector((state: RootState) => state.entity.userCity);
  const ao = useSelector((state: RootState) => state.entity.userAo);
  const entityType = useSelector((state: RootState) => state.entity.entityType);
  const [cities, setCities] = useState<CityData[]>([]);
  const [filteredCities, setFilteredCities] = useState<CityData[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [editingCity, setEditingCity] = useState<CityData | null>(null);

  const getCityStatsMutation = useGetCityStats({
    onSuccess: (data) => {
      const citiesArray = Array.isArray(data) ? data : [data];
      setCities(citiesArray);
      setFilteredCities(citiesArray);
      setLoading(false);
    },
    onError: (error: any) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || "שגיאה בטעינת הערים",
          toastType: "error",
        })
      );
      setLoading(false);
    },
  });

  useEffect(() => {
    // Check if user is super admin
    if (user.user_type !== "super_admin") {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: t('noPermissionsError'),
          toastType: "error",
        })
      );
      return;
    }

    fetchCities();
  }, [user.user_type]);

  const fetchCities = () => {
    setLoading(true);
    getCityStatsMutation.mutate({});
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value === "") {
      setFilteredCities(cities);
    } else {
      const searchLower = value.toLowerCase();
      setFilteredCities(
        cities.filter(
          (city) =>
            city.name.toLowerCase().includes(searchLower) ||
            (city.name_en && city.name_en.toLowerCase().includes(searchLower))
        )
      );
    }
  };

  const handleDataChange = () => {
    // Refresh the cities list after any changes
    fetchCities();
  };

  if (user.user_type !== "super_admin") {
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>{t("accessDenied")}</div>
          </div>
          <div className={styles.tabView}>
            <p>{t("accessDeniedMessage")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.title}>{cityParametersMode ? t("selectCityTitle") : t("title")}</div>
          <div className={styles.subtitle}>{cityParametersMode ? t("selectCitySubtitle") : t("subtitle")}</div>
        </div>
        <div className={styles.tabView}>
          <div className={styles.managment}>
            <div className={styles.searchRow}>
              <div className={styles.labelInput}>
                <div className={styles.text}>{t("searchPlaceholder")}</div>
                <SearchBar
                  options={cities.map((city) => ({
                    id: city.id,
                    name: city.name,
                  }))}
                  onOptionClick={() => {}}
                  value={searchText}
                  onChange={handleSearchChange}
                  isListNeeded={false}
                />
              </div>
              {!cityParametersMode && (
                <Button
                  text={t("addCityButton")}
                  icon="no"
                  onClick={() => setShowAddCityModal(true)}
                  className={styles.addButton}
                  id="add-city-btn"
                />
              )}
            </div>
            
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>{t("loadingCities")}</p>
              </div>
            ) : (
              <CityManagementTable 
                data={filteredCities} 
                onDataChange={handleDataChange}
                onRowClick={(city: CityData) => {
                  if (cityParametersMode) {
                    navigate(`/Weitz/managment/city-parameters/${city.id}`);
                  } else {
                    setEditingCity(city);
                  }
                }}
              />
            )}
            
            {!loading && filteredCities.length === 0 && (
              <div className={styles.noData}>
                <p>{searchText ? t("noSearchResults") : t("noCitiesFound")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add/Edit City Modal */}
      {!cityParametersMode && (
        <AddCityModal
          isOpen={showAddCityModal || editingCity !== null}
          editCity={editingCity}
          onClose={() => {
            setShowAddCityModal(false);
            setEditingCity(null);
          }}
          onSuccess={() => {
            setShowAddCityModal(false);
            setEditingCity(null);
            fetchCities();
          }}
        />
      )}
    </div>
  );
};
